import { useEffect, useMemo } from 'react'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'

type CompletionRateProps = {
  title: string
  current: number
  total: number
  unit: string
}

const SPRING_CONFIG = { damping: 100, stiffness: 1000 }
const CHART_COLORS = ['white', 'rgba(255, 255, 255, 0.6)'] as const

const useAnimatedValue = (value: number, formatter: (v: number) => string) => {
  const motionValue = useMotionValue(0)
  const springValue = useSpring(motionValue, SPRING_CONFIG)
  const animated = useTransform(springValue, formatter)

  useEffect(() => {
    motionValue.set(value)
  }, [value, motionValue])

  return animated
}

export const CompletionRate = ({
  title,
  current,
  total,
  unit = '',
}: CompletionRateProps) => {
  const calculatedPercent =
    total > 0 ? Number(((current / total) * 100).toFixed(2)) : 0

  const animatedPercent = useAnimatedValue(calculatedPercent, (v) =>
    v.toLocaleString('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    })
  )

  const animatedCurrent = useAnimatedValue(current, (v) =>
    Math.round(v).toLocaleString('en-US')
  )

  const animatedTotal = useAnimatedValue(total, (v) =>
    Math.round(v).toLocaleString('en-US')
  )

  const chartData = useMemo(
    () => [
      { name: 'completed', value: calculatedPercent },
      { name: 'remaining', value: 100 - calculatedPercent },
    ],
    [calculatedPercent]
  )

  return (
    <div className="flex flex-1 h-full p-8 flex-col gap-4 justify-between  rounded-xl bg-yellow-blue-gradient">
      <div>
        <div className="text-xl font-semibold text-[var(--Component-colors-Alpha-alpha-black-70)]">
          {title}
        </div>

        <div className="text-6xl font-semibold text-[var(--Component-colors-Alpha-alpha-black-90)]">
          <motion.span>{animatedCurrent}</motion.span>/
          <motion.span>{animatedTotal}</motion.span>
          <span className="text-3xl ml-1 font-semibold text-[var(--Component-colors-Alpha-alpha-black-70)]">
            {unit}
          </span>
        </div>
      </div>

      <div className="relative w-[200px] h-[200px] ml-auto">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={100}
              paddingAngle={0}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
            >
              {chartData.map(({ name }, index) => (
                <Cell key={name} fill={CHART_COLORS[index]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <span className="text-4xl font-semibold text-tertiary-900">
            <motion.span>{animatedPercent}</motion.span>%
          </span>
        </div>
      </div>
    </div>
  )
}
